<script setup lang="ts">
import type { QueueTrack } from '~/composables/audio/queue'
import type { Track, Album } from '~/types'

import { useI18n } from 'vue-i18n'
import { useStore } from '~/store'
import { computed, ref, useAttrs } from 'vue'

import { useContext } from '~/ui/context'
import Button from '~/ui/Button.vue'

// TODO: Refactor this:
// 1. Simplify model
// - Turn `store.dispatch('favorites/toggle', track.id)` into v-model
// - This removes need for `store.getters['favorites/isFavorite']`
// 2. Simplify props
// - Use simpler defaulting `const { a=b, ...props } = defineProps...`
// - Turn presentational props into classes and ui-attributes where appropriate
// - Instead of misleading boolean `button`, use `label` or simply use the slot
// - Possible: Add global color pink
// 3. Inline the animation logic here and dissolve _favorite.scss
// - Consolidate confusing 'really', 'favorited', 'inverted' and 'pink' classes
// - Remove semantically redundant classes 'labeled', 'button', 'ui' and 'icon'
// 4. SImplify template
// - No need for two separate buttons
// - If we add `color-pink` to the design system, and animate the icon, then we can use the default Button component (or better: Switch)
// - Add accessible state indicator ('aria-pressed')

defineOptions({ inheritAttrs: false })

interface Props {
  track?: QueueTrack | Track | {id: Track['id']}
  album?: Album
  button?: boolean
  border?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  track: () => ({} as Track),
  button: false,
  border: false,
})

const attrs = useAttrs()

const isGhost = computed(() => {
  const cls = attrs.class
  if (typeof cls === 'string') return cls.includes('ghost')
  if (Array.isArray(cls)) return cls.some(c => typeof c === 'string' && c.includes('ghost'))
  return false
})

const { t } = useI18n()
const store = useStore()

const albumTracks = computed(() => props.album?.tracks ?? [])

const isFavorite = computed(() => {
  if (props.album) {
    return albumTracks.value.length > 0 && albumTracks.value.every(t => store.getters['favorites/isFavorite'](t.fid))
  }
  return store.getters['favorites/isFavorite'](props.track?.id)
})

const toggle = () => {
  if (props.album) {
    const shouldFavorite = !isFavorite.value
    for (const track of albumTracks.value) {
      const currentlyFavorited = store.getters['favorites/isFavorite'](track.fid)
      if (shouldFavorite !== currentlyFavorited) {
        store.dispatch('favorites/toggle', track.fid)
      }
    }
  } else {
    store.dispatch('favorites/toggle', props.track?.id)
  }
}
const isHovered = ref(false)
const title = computed(() => isFavorite.value
  ? t('components.favorites.TrackFavoriteIcon.button.remove')
  : t('components.favorites.TrackFavoriteIcon.button.add')
)

// TODO: Handle uiBlockSize as a prop instead, for type safety
const uiBlockSize = (attrs['ui-block-size'] ?? useContext().value.uiBlockSize ?? '48') as '24' | '40' | '48'
</script>

<template>
  <Button
    v-if="button"
    :aria-pressed="isFavorite"
    v-bind="attrs"
    @click.stop="toggle"
  >
    <span class="favorite-icon" :class="{ favorited: isFavorite }" style="display: inline-flex">
      <Icon :icon="isFavorite ? 'bi:heart-fill' : 'bi:heart'" />
    </span>
    <span>
      {{ isFavorite
        ? t('components.favorites.TrackFavoriteIcon.label.inFavorites')
        : t('components.favorites.TrackFavoriteIcon.button.add') }}
    </span>
  </Button>
  <Button
    v-else
    class="shape-square"
    :aria-pressed="isGhost ? undefined : isFavorite"
    :aria-label="title"
    :title="title"
    v-bind="attrs"
    @click.stop="toggle"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    :ui-block-size
  >
    <span class="favorite-icon" :class="{ favorited: isFavorite }" style="display: inline-flex">
      <Icon :icon="isFavorite ? 'bi:heart-fill' : 'bi:heart'" />
    </span>
  </Button>
</template>
